import React from 'react'
import styled from 'styled-components'
import { IconButton } from 'components/Button'
import { ReactComponent as PlusIcon } from 'icons/circle.plus.svg'
import { ReactComponent as MinusIcon } from 'icons/circle.minus.svg'
import { noop } from 'utils'
import Dropdown from 'components/Dropdown'
import { DropdownItem } from 'components/Dropdown/Item'
import { useKey } from 'react-use'
import { keyWithoutModifier } from 'utils/keyboardEvents'

import { MIN_ZOOM, MAX_ZOOM } from './constants'
export interface ZoomToFit {
  type: 'ZOOM_TO_FIT'
  factor?: number
}

export interface ZoomFactor {
  type: 'ZOOM_FACTOR'
  factor: number
}

export type ZoomTypes = ZoomToFit | ZoomFactor

const Container = styled.div`
  height: 26px;
  width: 100px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 16px;
`

const IconContainer = styled(IconButton)`
  flex-shrink: 0;
  margin: 1px; /* stylelint-disable-line scales/space */
  color: ${({ theme }) => theme.colors.text.dark.secondary};

  &:hover {
    color: ${({ theme }) => theme.colors.text.dark.base};
  }

  &:disabled {
    opacity: 0.5;
  }
`

interface ZoomDropdownItemProps {
  active?: boolean
}

const ZoomDropdownItem = styled(DropdownItem)<ZoomDropdownItemProps>`
  min-width: 6rem;
  color: ${({ theme, active }) => active && theme.colors.primary};
`

const ExpandedDropdown = styled(Dropdown)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-basis: 100%;
  font-size: ${({ theme }) => theme.fontSizes.smaller};
  font-family: ${({ theme }) => theme.fonts.buttons};
  cursor: pointer;
  font-variant-numeric: tabular-nums;
`

const nearestPowerOf2 = (value: number): number => {
  return 1 << (31 - Math.clz32(value))
}

const clamp = (min: number, max: number, value: number): number =>
  Math.max(min, Math.min(value, max))

const clampFactor = (factor: number, min: number, max: number): number => {
  if (factor <= min) {
    return min
  } else if (factor <= 1) {
    return factor
  }

  return nearestPowerOf2(clamp(1, max, factor))
}

const isMin = (zoom: ZoomTypes, min: number) => {
  return zoom && zoom.factor && zoom.factor <= min ? true : false
}

const isMax = (zoom: ZoomTypes, max: number) => {
  return zoom && zoom.factor && zoom.factor >= max ? true : false
}

export type ZoomProps = {
  zoom: ZoomTypes
  onZoomChanged: (newZoom: ZoomTypes) => void
  minZoom?: number
  maxZoom?: number
  navigator?: Pick<Navigator, 'platform'>
}
const Zoom: React.FC<ZoomProps> = ({
  zoom,
  onZoomChanged,
  navigator = window.navigator,
  minZoom = MIN_ZOOM,
  maxZoom = MAX_ZOOM,
}) => {
  const factors = [minZoom, 0.5, 1.0, 2.0, 4.0, maxZoom]
  const zoomOut = () => {
    if (typeof zoom.factor !== 'number' || zoom.factor <= factors[0]) {
      return
    }
    const nextFactorIndex = factors.findIndex(
      element => element >= (zoom.factor || 0)
    )
    changeZoom(factors[nextFactorIndex - 1])
  }
  const zoomIn = () => {
    if (
      typeof zoom.factor !== 'number' ||
      zoom.factor >= factors[factors.length - 1]
    ) {
      return
    }
    const nextFactorIndex = factors.findIndex(
      element => element > (zoom.factor || 0)
    )
    changeZoom(factors[nextFactorIndex])
  }

  const zoomActualSize = () => {
    changeZoom(1)
  }

  const zoomToFit = () => {
    onZoomChanged({ type: 'ZOOM_TO_FIT' })
  }

  const changeZoom = (newFactor: number) => {
    onZoomChanged({
      type: 'ZOOM_FACTOR',
      factor: clampFactor(newFactor, minZoom, maxZoom),
    })
  }

  const formatZoom = (zoom: ZoomTypes) => {
    if (typeof zoom.factor !== 'number') {
      return '-'
    }

    return `${Math.round(zoom.factor * 100)}%`
  }

  useKey(keyWithoutModifier(['=', '+'], navigator), zoomIn)
  useKey(keyWithoutModifier('-', navigator), zoomOut)
  useKey(keyWithoutModifier('0', navigator), zoomActualSize)
  useKey(keyWithoutModifier('1', navigator), zoomToFit)

  return (
    <Container>
      <IconContainer
        icon={MinusIcon}
        size={20}
        onClick={zoomOut}
        disabled={isMin(zoom, minZoom)}
        data-testid="zoom-out"
      />
      <ExpandedDropdown
        toggle={<>{formatZoom(zoom)}</>}
        onToggle={noop}
        minWidth={'6rem'}
        placement="bottom"
        data-testid="zoom-value">
        <ZoomDropdownItem
          className={
            zoom.type === 'ZOOM_FACTOR' && zoom.factor === 0.5 ? 'active' : ''
          }
          onClick={() => changeZoom(0.5)}>
          50%
        </ZoomDropdownItem>
        <ZoomDropdownItem
          className={
            zoom.type === 'ZOOM_FACTOR' && zoom.factor === 1 ? 'active' : ''
          }
          onClick={() => changeZoom(1)}>
          100%
        </ZoomDropdownItem>
        <ZoomDropdownItem
          className={
            zoom.type === 'ZOOM_FACTOR' && zoom.factor === 2 ? 'active' : ''
          }
          onClick={() => changeZoom(2)}>
          200%
        </ZoomDropdownItem>
        <Dropdown.Divider />
        <ZoomDropdownItem
          className={zoom.type === 'ZOOM_TO_FIT' ? 'active' : ''}
          onClick={() => onZoomChanged({ type: 'ZOOM_TO_FIT' })}>
          Zoom to Fit
        </ZoomDropdownItem>
      </ExpandedDropdown>
      <IconContainer
        icon={PlusIcon}
        size={20}
        onClick={zoomIn}
        disabled={isMax(zoom, maxZoom)}
        data-testid="zoom-in"
      />
    </Container>
  )
}

export default Zoom
